/* Dialog behavior only. Native product state and trigger placement are separate. */
(() => {
  'use strict';
  if (customElements.get('fizzy-size-guide')) return;
  class SizeGuide extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const dialog = this.querySelector('dialog');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      const select = index => {
        tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
          if (panel) panel.hidden = i !== index;
        });
      };
      // The adapter can move the original trigger beside the native size label.
      const root = this.closest('[data-teeinblue-pdp]');
      this.bindPhysicalGuide(root);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-size-unit-button]');
        const table = button?.closest('[data-size-table]');
        if (!table || !this.contains(table)) return;
        const unit = button.dataset.sizeUnitButton;
        if (unit !== 'cm' && unit !== 'in') return;
        table.dataset.sizeUnit = unit;
        table.querySelectorAll('[data-size-unit-button]').forEach(item => {
          item.setAttribute('aria-pressed', String(item.dataset.sizeUnitButton === unit));
        });
        table.querySelectorAll('[data-size-unit-label]').forEach(label => { label.textContent = unit; });
        table.querySelectorAll('[data-size-cm]').forEach(cell => {
          const cm = Number(cell.dataset.sizeCm);
          if (Number.isFinite(cm)) cell.textContent = (unit === 'in' ? cm / 2.54 : cm).toLocaleString(document.documentElement.lang || undefined, { maximumFractionDigits: 1 });
        });
      }, options);
      const triggers = [...(root?.querySelectorAll('[data-size-guide-open]') || [])].filter(node => node.getAttribute('aria-controls') === dialog.id);
      const trigger = triggers.length === 1 ? triggers[0] : null;
      trigger?.addEventListener('click', () => {
        if (dialog.open || !dialog.isConnected) return;
        dialog.showModal();
      }, options);
      this.querySelector('[data-size-guide-close]')?.addEventListener('click', () => dialog.close(), options);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, options);
      // Selecting a tab block in Shopify previews that content directly.
      document.addEventListener('shopify:block:select', event => {
        const blockId = event.detail?.blockId;
        if (!blockId || !this.isConnected) return;
        const panel = this.querySelector(`[data-size-guide-block="${CSS.escape(blockId)}"]`);
        if (!panel) return;
        const index = tabs.findIndex(tab => tab.getAttribute('aria-controls') === panel.id);
        if (index >= 0) select(index);
        if (!dialog.open) dialog.showModal();
      }, options);
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(index), options);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault(); select(next); tabs[next].focus();
        }, options);
      });
      this.setAttribute('data-size-guide-ready', '');
      this.dispatchEvent(new CustomEvent('fizzy:size-guide:ready', { bubbles: true }));
    }

    bindPhysicalGuide(root) {
      this.physicalObserver?.disconnect();
      cancelAnimationFrame(this.physicalFrame);
      const target = this.querySelector('[data-size-guide-dynamic]');
      const legacy = this.querySelector('[data-size-guide-primary-fallback]') || this.querySelector('[data-size-guide-static]');
      if (!root || !target || !legacy) return;
      let previous = null;
      const update = () => {
        this.physicalFrame = null;
        const templates = [...this.querySelectorAll('template[data-size-guide-base]')];
        const active = [...root.querySelectorAll('.tee-radio.active input[id^="teeinblue-available-prod-"]')];
        const selected = active.length ? active : [...root.querySelectorAll('input[id^="teeinblue-available-prod-"]:checked')];
        let base = selected.length === 1 ? selected[0].value : '';
        // A single-base Campaign has no native product selector. Use its sole referenced profile.
        if (!base && !root.querySelector('input[id^="teeinblue-available-prod-"]') && templates.length === 1) {
          base = templates[0].dataset.sizeGuideBase;
        }
        let matches = templates.filter(t => t.dataset.sizeGuideBase === base && base);
        // Reuse the verified physical-product registry for alternate print-position bases.
        if (!matches.length && base) {
          const registry = root.querySelector('fizzy-physical-content');
          const physicalId = window.FizzyPhysicalContent
            ? window.FizzyPhysicalContent.resolve(base, window.FizzyPhysicalContent.sources(registry))
            : null;
          const sources = registry ? [...registry.querySelectorAll('template[data-physical-id]')] : [];
          const physical = sources.filter(t => t.dataset.physicalId === physicalId);
          if (physicalId && physical.length === 1) {
            matches = templates.filter(t => t.dataset.sizeGuidePhysical === physicalId || t.dataset.sizeGuideBase === physical[0].dataset.baseId);
          }
        }
        const template = matches.length === 1 && matches[0].content.querySelector('.pdp-size-guide__image, [data-size-table]') ? matches[0] : null;
        const signature = base + ':' + (template ? templates.indexOf(template) : 'missing');
        if (signature === previous) return;
        previous = signature;
        // Only replace editor tabs when a verified table exists for this base.
        legacy.hidden = Boolean(template);
        target.hidden = !template;
        target.replaceChildren();
        if (template) {
          target.append(template.content.cloneNode(true));
        }
        target.dataset.activeBase = base;
      };
      const schedule = () => {
        if (this.physicalFrame == null) this.physicalFrame = requestAnimationFrame(update);
      };
      root.addEventListener('change', schedule, { signal: this.abort.signal });
      root.addEventListener('click', schedule, { signal: this.abort.signal });
      this.physicalObserver = new (window.FizzyProductObserver?.Observer || MutationObserver)(schedule);
      this.physicalObserver.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'checked', 'value'] });
      update();
    }

    disconnectedCallback() {
      this.physicalObserver?.disconnect();
      cancelAnimationFrame(this.physicalFrame);
      this.abort?.abort();
      this.removeAttribute('data-size-guide-ready');
      const dialog = this.querySelector('dialog');
      if (dialog?.open) dialog.close();
    }
  }
  customElements.define('fizzy-size-guide', SizeGuide);
})();
